"use client";

import { getProcessIcon } from "@/lib/icons";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/ui/animated-section";

interface ProcessStep {
 title: string;
 description: string;
}

interface ProcessTimelineProps {
 title?: string;
 subtitle?: string;
 steps: ProcessStep[];
}

export function ProcessTimeline({ title = "Our Process", subtitle, steps }: ProcessTimelineProps) {
 if (steps.length === 0) return null;

 return (
 <section className="py-16 lg:py-20">
 {/* Header */}
 <AnimatedSection className="text-center max-w-3xl mx-auto mb-14">
 <span className="text-sm font-semibold text-primary uppercase tracking-wider">How We Work</span>
 <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-foreground">{title}</h2>
 {subtitle && <p className="mt-3 text-lg text-muted-foreground">{subtitle}</p>}
 </AnimatedSection>

 {/* Desktop: Horizontal infographic (hidden on mobile) */}
 <div className="hidden lg:block">
  <StaggerContainer className="relative" staggerDelay={0.15}>
   {/* Connecting line */}
     <div className="absolute top-[52px] left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

   <div className={`grid gap-4 ${steps.length <= 4 ? "grid-cols-" + steps.length : steps.length <= 6 ? "grid-cols-3" : "grid-cols-4"}`}
    style={{ gridTemplateColumns: `repeat(${Math.min(steps.length, 6)}, minmax(0, 1fr))` }}>
    {steps.map((step, i) => {
     const Icon = getProcessIcon(step.title);
     return (
      <StaggerItem key={i} className="relative flex flex-col items-center text-center group">
       {/* Step number */}
       <div className="text-xs font-bold text-primary/60 mb-2 tracking-widest">
        {String(i + 1).padStart(2, "0")}
       </div>

       {/* Icon circle */}
    <div className="service-accent-gradient service-accent-shadow relative z-10 mb-5 flex h-[72px] w-[72px] items-center justify-center rounded-2xl group-hover:scale-110 transition-all duration-300">
        <Icon className="w-7 h-7 text-white" />
       </div>

       {/* Arrow connector (not on last) */}
       {i < steps.length - 1 && (
        <div className="absolute top-[52px] -right-2 translate-x-1/2 z-20 hidden lg:block">
         <svg width="16" height="16" viewBox="0 0 16 16" className="text-primary/40">
          <path d="M4 2 L12 8 L4 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
         </svg>
        </div>
       )}

       {/* Content card */}
       <div className="relative rounded-xl p-px bg-gradient-to-b from-border to-transparent group-hover:from-primary/30 group-hover:to-primary/5 transition-all duration-500 flex-1">
        <div className="h-full bg-card rounded-[calc(0.75rem-1px)] p-5">
        <h3 className="font-semibold text-foreground mb-2 text-sm">{step.title}</h3>
        <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
        </div>
       </div>
      </StaggerItem>
     );
    })}
   </div>
  </StaggerContainer>
 </div>

 {/* Mobile/Tablet: Vertical timeline */}
 <div className="lg:hidden">
  <StaggerContainer className="relative pl-8 md:pl-12" staggerDelay={0.12}>
   {/* Vertical line */}
     <div className="absolute left-[18px] md:left-[26px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-primary to-primary/20" />

   <div className="space-y-6">
    {steps.map((step, i) => {
     const Icon = getProcessIcon(step.title);
     return (
      <StaggerItem key={i} className="relative flex gap-4 md:gap-6 items-start">
       {/* Node */}
       <div className="absolute -left-8 md:-left-12 flex flex-col items-center">
        <div className="service-accent-gradient service-accent-shadow z-10 flex h-10 w-10 items-center justify-center rounded-xl md:h-12 md:w-12">
         <Icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
        </div>
       </div>

       {/* Card */}
       <div className="flex-1 bg-card border border-border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center gap-3 mb-2">
         <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
          {String(i + 1).padStart(2, "0")}
         </span>
         <h3 className="font-semibold text-foreground">{step.title}</h3>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
       </div>
      </StaggerItem>
     );
    })}
   </div>
  </StaggerContainer>
 </div>
 </section>
 );
}
